import * as React from 'react';
import { cn } from './lib/utils';

/**
 * Consistent numeric display. Enforces tabular-nums and locale formatting.
 *
 * <StatValue value={1842} />                         → "1,842"
 * <StatValue value={1842} abbreviate />              → "1.8K"
 * <StatValue value={1200000} abbreviate />           → "1.2M"
 * <StatValue value={0.864} format="percent" />       → "86.4%"
 * <StatValue value={0.864} format="percent" decimals={0} /> → "86%"
 * <StatValue value={312} format="currency" />        → "$312"
 * <StatValue value={3.14} format="decimal" decimals={1} /> → "3.1"
 * <StatValue value={4.2} unit="d" />                 → "4.2d"  (custom unit)
 *
 * className applied to the outer <span>.
 * All other props forwarded to <span>.
 */

function abbreviate(n) {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${+(n / 1_000_000).toPrecision(3)}M`;
  if (abs >= 1_000)     return `${+(n / 1_000).toPrecision(3)}K`;
  return String(n);
}

function format(value, fmt, decimals, abbreviateFlag, unit) {
  if (value === null || value === undefined) return '—';

  const n = Number(value);
  if (Number.isNaN(n)) return String(value);

  if (abbreviateFlag) return abbreviate(n);

  switch (fmt) {
    case 'percent': {
      const pct = n > 1 ? n : n * 100;
      const d = decimals ?? (Number.isInteger(pct) ? 0 : 1);
      return `${pct.toFixed(d)}%`;
    }
    case 'currency': {
      const d = decimals ?? 0;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      }).format(n);
    }
    case 'decimal': {
      const d = decimals ?? 2;
      return n.toFixed(d);
    }
    default: {
      // integer with locale separators
      const d = decimals ?? (Number.isInteger(n) ? 0 : 1);
      const formatted = d === 0
        ? new Intl.NumberFormat('en-US').format(Math.round(n))
        : n.toFixed(d);
      return unit ? `${formatted}${unit}` : formatted;
    }
  }
}

function StatValue({
  value,
  format: fmt = 'number',
  decimals,
  abbreviate: abbreviateFlag = false,
  unit,
  className,
  ...props
}) {
  const display = format(value, fmt, decimals, abbreviateFlag, unit);

  return (
    <span
      className={cn('tabular-nums', className)}
      {...props}
    >
      {display}
    </span>
  );
}

export { StatValue };
